import React, { useState } from 'react';
import { ArrowRight, Check, Lock, Download, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import colors from '../config/colors';

const ResultsReportMockup = () => {
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Sample matched school data (from matching algorithm)
  const topMatch = {
    school_name: "Garden International School",
    location: "Mont Kiara",
    curriculum: ["British", "IGCSE", "A-Level"],
    grade_range: { min: 3, max: 13 },
    fees: {
      annual_tuition: 45000,
      registration_fee: 5000,
      deposit: 10000
    },
    match_score: 34,
    score_breakdown: {
      priority_score: 28,
      profile_bonus: 4,
      curriculum_bonus: 2,
      total_score: 34
    },
    strengths: [
      "academic_excellence",
      "nurturing_environment",
      "diverse_community",
      "university_placement",
      "strong_cocurricular"
    ],
    why_matched: [
      "Strong academic rigour (your #1 priority)",
      "Excellent pastoral care system (your #2 priority)",
      "Highly diverse international community",
      "Outstanding university placement record",
      "Matches your budget range (RM30-50k)"
    ],
    university_destinations: ["UK (Oxford, Cambridge, LSE)", "US (Ivy League)", "Australia (Go8)"],
    class_size: "18-22 students",
    contact: {
      phone: "+60 3-6209 6888",
      email: "admissions@gardenschool.edu.my",
      website: "www.gardenschool.edu.my"
    }
  };

  // Locked schools (for tier 2 upgrade)
  const lockedSchools = [
    { school_name: "The International School of Kuala Lumpur", match_score: 32, location: "Ampang" },
    { school_name: "Mont'Kiara International School", match_score: 30, location: "Mont Kiara" },
    { school_name: "Fairview International School", match_score: 28, location: "Subang Jaya" },
    { school_name: "Nexus International School", match_score: 27, location: "Putrajaya" }
  ];

  const childName = "Maya"; // From questionnaire

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bgPage,
      padding: '40px 20px'
    }}>
      {/* Header */}
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '600',
            color: colors.brandPrimary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '12px'
          }}>
            Your School Shortlist
          </div>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            fontWeight: '700',
            color: colors.textPrimary,
            marginBottom: '16px',
            lineHeight: '1.2'
          }}>
            We found the perfect match for {childName}
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: colors.textSecondary,
            lineHeight: '1.6'
          }}>
            Based on your priorities, {childName}'s profile, and your budget, here's your top recommendation.
          </p>
        </div>

        {/* Top Match Card */}
        <div style={{
          background: colors.textInverse,
          borderRadius: '20px',
          padding: 'clamp(32px, 5vw, 48px)',
          marginBottom: '32px',
          border: `3px solid ${colors.brandPrimary}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
        }}>
          {/* Best Match Badge */}
          <div style={{
            display: 'inline-block',
            background: colors.brandPrimary,
            color: colors.textInverse,
            padding: '8px 20px',
            borderRadius: '100px',
            fontSize: '0.85rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '24px'
          }}>
            ✨ Best Match • {topMatch.match_score} points
          </div>

          {/* School Name & Location */}
          <h2 style={{
            fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
            fontWeight: '700',
            color: colors.textPrimary,
            marginBottom: '8px'
          }}>
            {topMatch.school_name}
          </h2>
          <div style={{
            fontSize: '1.1rem',
            color: colors.textMuted,
            marginBottom: '24px'
          }}>
            📍 {topMatch.location} • {topMatch.curriculum.join(" • ")}
          </div>

          {/* Why This School Matched */}
          <div style={{
            background: colors.bgAccent,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: colors.textPrimary,
              marginBottom: '16px'
            }}>
              Why this school is perfect for {childName}:
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {topMatch.why_matched.map((reason) => (
                <li key={reason} style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '12px',
                  fontSize: '1.05rem',
                  color: colors.textSecondary,
                  alignItems: 'flex-start'
                }}>
                  <Check size={20} color={colors.brandPrimary} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Key Details Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '6px', fontWeight: '600' }}>
                Annual Tuition
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.brandPrimary }}>
                RM {topMatch.fees.annual_tuition.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: colors.textMuted, marginTop: '4px' }}>
                Within your budget ✓
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '6px', fontWeight: '600' }}>
                Class Size
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.textPrimary }}>
                {topMatch.class_size}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '6px', fontWeight: '600' }}>
                Grade Range
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.textPrimary }}>
                Year {topMatch.grade_range.min}-{topMatch.grade_range.max}
              </div>
            </div>
          </div>

          {/* Match Score Breakdown */}
          <div style={{
            border: `2px solid ${colors.borderMedium}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h4 style={{
              fontSize: '1rem',
              fontWeight: '700',
              color: colors.textPrimary,
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}
            onClick={() => setShowUpgrade(!showUpgrade)}
            >
              Match Score Breakdown
              {showUpgrade ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </h4>
            
            {showUpgrade && (
              <div>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: colors.textSecondary }}>Your Top 5 Priorities</span>
                  <span style={{ fontWeight: '700', color: colors.brandPrimary }}>{topMatch.score_breakdown.priority_score} pts</span>
                </div>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: colors.textSecondary }}>Profile Match Bonus</span>
                  <span style={{ fontWeight: '700', color: colors.brandPrimary }}>+{topMatch.score_breakdown.profile_bonus} pts</span>
                </div>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '16px', borderBottom: `1px solid ${colors.borderMedium}` }}>
                  <span style={{ color: colors.textSecondary }}>Curriculum Bonus</span>
                  <span style={{ fontWeight: '700', color: colors.brandPrimary }}>+{topMatch.score_breakdown.curriculum_bonus} pts</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>Total Match Score</span>
                  <span style={{ fontWeight: '700', fontSize: '1.4rem', color: colors.brandPrimary }}>{topMatch.score_breakdown.total_score} pts</span>
                </div>
              </div>
            )}
          </div>

          {/* University Destinations */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: colors.textPrimary,
              marginBottom: '12px'
            }}>
              Where students go:
            </h4>
            <div style={{
              fontSize: '1.05rem',
              color: colors.textSecondary,
              lineHeight: '1.6'
            }}>
              {topMatch.university_destinations.join(" • ")}
            </div>
          </div>

          {/* Contact CTA */}
          <div style={{
            background: 'linear-gradient(135deg, #FFE5D0 0%, #FFDDB8 100%)',
            borderRadius: '12px',
            padding: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: '1.1rem', marginBottom: '4px' }}>
                Ready to visit?
              </div>
              <div style={{ fontSize: '0.95rem', color: colors.textMuted }}>
                {topMatch.contact.phone} • {topMatch.contact.email}
              </div>
            </div>
            <a 
              href={`https://${topMatch.contact.website}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: colors.brandPrimary,
                color: colors.textInverse,
                padding: '12px 24px',
                borderRadius: '100px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s'
              }}
            >
              Visit Website
              <ArrowRight size={18} />
            </a>
          </div>
        </div>

        {/* Upgrade Section - 4 More Schools */}
        <div style={{
          background: 'linear-gradient(135deg, #2C2823 0%, #3D3834 100%)',
          borderRadius: '20px',
          padding: 'clamp(32px, 5vw, 48px)',
          color: colors.textInverse,
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '40px'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            fontSize: '180px',
            opacity: '0.1'
          }}>
            🔒
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{
              fontSize: 'clamp(1.6rem, 3vw, 2rem)',
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              See 4 More Perfectly Matched Schools
            </h3>
            <p style={{
              fontSize: '1.1rem',
              opacity: '0.9',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Unlock your complete shortlist with detailed comparisons, fee breakdowns, and personalized recommendations.
            </p>

            {/* Locked Schools Preview */}
            <div style={{
              display: 'grid',
              gap: '12px',
              marginBottom: '32px'
            }}>
              {lockedSchools.map((school) => (
                <div key={school.school_name} style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Lock size={20} opacity={0.7} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '1.05rem' }}>
                        {school.school_name}
                      </div>
                      <div style={{ fontSize: '0.9rem', opacity: '0.7' }}>
                        {school.location}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#FFD700'
                  }}>
                    {school.match_score} pts
                  </div>
                </div>
              ))}
            </div>

            {/* Upgrade CTA */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '4px' }}>
                  Upgrade for just +RM10
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.8' }}>
                  Compare schools side-by-side • Download PDF report • Email support
                </div>
              </div>
              <button style={{
                background: '#FFD700',
                color: '#2C2823',
                padding: '16px 32px',
                borderRadius: '100px',
                fontWeight: '700',
                fontSize: '1.1rem',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s'
              }}>
                Unlock Full Report
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Email Download Section */}
        <div style={{
          background: colors.textInverse,
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center',
          border: `2px solid ${colors.borderMedium}`
        }}>
          <Mail size={48} color={colors.brandPrimary} style={{ margin: '0 auto 16px' }} />
          <h4 style={{
            fontSize: '1.4rem',
            fontWeight: '700',
            color: colors.textPrimary,
            marginBottom: '12px'
          }}>
            Want this report in your inbox?
          </h4>
          <p style={{
            fontSize: '1rem',
            color: colors.textSecondary,
            marginBottom: '24px'
          }}>
            We'll send you a PDF version of your personalized school report
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            maxWidth: '500px',
            margin: '0 auto',
            flexWrap: 'wrap'
          }}>
            <input
              type="email"
              placeholder="Enter your email"
              style={{
                flex: 1,
                minWidth: '250px',
                padding: '14px 20px',
                borderRadius: '100px',
                border: `2px solid ${colors.borderMedium}`,
                fontSize: '1rem',
                outline: 'none'
              }}
            />
            <button style={{
              background: colors.brandPrimary,
              color: colors.textInverse,
              padding: '14px 32px',
              borderRadius: '100px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Download size={18} />
              Send Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsReportMockup;
